/**
 * Places client that talks only to this plugin's backend.
 *
 * No Google key is ever present in the browser: the backend endpoints call
 * Google with the private server key and return data in the shape this
 * component has always consumed.
 */

import { captchaHeaders } from "../../captcha";

function backendBase() {
	const pluginUrl =
		(window.wpApiSettings && window.wpApiSettings.pluginUrl) ||
		"/wp-content/plugins/wp-id-generator/";
	return `${pluginUrl}backend/public`;
}

function newSessionToken() {
	if (window.crypto && typeof window.crypto.randomUUID === "function") {
		return window.crypto.randomUUID();
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export default class GoogleMapsApi {
	_sessionToken = null;
	_failed = false;

	getSessionToken() {
		if (!this._sessionToken) {
			this._sessionToken = newSessionToken();
		}
		return this._sessionToken;
	}

	resetSessionToken() {
		this._sessionToken = null;
	}

	async _request(path, params) {
		const query = new URLSearchParams(params).toString();
		const headers = await captchaHeaders();
		const response = await fetch(`${backendBase()}/${path}?${query}`, {
			credentials: "same-origin",
			headers,
		});
		let payload = null;
		try {
			payload = await response.json();
		} catch (e) {
			payload = null;
		}
		if (!response.ok) {
			// Configuration problems (missing key, blocked key) are permanent for this page load.
			if (response.status === 500 || response.status === 502) {
				this._failed = true;
			}
			const message = (payload && payload.error) || `Request failed (${response.status})`;
			const error = new Error(message);
			error.status = response.status;
			error.captcha = payload && payload.captcha;
			throw error;
		}
		return payload;
	}

	async fetchPlaces(businessName) {
		if (this._failed) {
			return Promise.reject(new MapsApiFailure());
		}
		try {
			const data = await this._request("places-autocomplete.php", {
				input: businessName,
				session: this.getSessionToken(),
			});
			return (data && data.predictions) || [];
		} catch (error) {
			console.error("Error fetching places:", error);
			if (this._failed) {
				throw new MapsApiFailure();
			}
			throw error;
		}
	}

	async getPlaceDetails(placeId) {
		if (this._failed) {
			return Promise.reject(new MapsApiFailure());
		}
		try {
			const place = await this._request("places-api-v2.php", {
				place_id: placeId,
				session: this.getSessionToken(),
			});
			this.resetSessionToken();
			return place;
		} catch (error) {
			console.error("Error getting place details:", error);
			this.resetSessionToken();
			throw error;
		}
	}
}

export class MapsApiFailure {
	message = "Google Maps API failure";
}
